import AlertPopup from "components/alert-popup/AlertPopup";
import ClientGeneralInfo from "components/sections-client/ClientGeneralInfo";
import InfoContext from "pages/InfoContext";
import InfoHeaderAction from "pages/info-header/InfoHeaderAction";
import MissionsWorkers from "components/workers/MissionsWorkers";
import _ from "lodash";
import { ENTITY } from "constants/info-header";
import { FormattedMessage } from "react-intl";
import { Link, useParams, useNavigate } from "react-router-dom";
import { formatSentData } from "utils/clientHelper";
import { getFormattedDate } from "utils/functions";
import { getStatusLabel } from "utils/workerHelper";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useEffect, useMemo, useState, useContext } from "react";
import { useFileContext } from "api/files/FilesProvider";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useMissionContext } from "api/mission/MissionProvider";
import LoaderV2 from "core/loader/LoaderV2";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useSectorContext } from "api/settings/sector/SectorProvider";
import { useUserContext } from "api/user/UserProvider";
import { useWindowSize, useEntityProfilePicture } from "utils/customHooks";
import { QUERY_STATUS, SECTIONS } from "constants/constants";
import { useForm } from "react-hook-form";
import { CLIENT_FORM_DEFAULT_VALUES } from "constants/forms";
import PageContext from "pages/PageContext";
import { useBillingClientDatatrackContext } from "api/datatrack/client/BillingClientDatatrackProvider";
import ValidationModal from "components/modal/ValidationModal";
import { useSettingsContext } from "api/settings/SettingsProvider";
import {
  commonOnCloseHandler,
  validationModalTitleHelper,
} from "utils/modalsHelper";
import { statusHandler } from "utils/helper";

const DisplayClient = () => {
  const navigate = useNavigate();
  const {
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: CLIENT_FORM_DEFAULT_VALUES,
  });
  const { slug } = useParams();
  const {
    getTimeline,
    resetTimeline,
    timelineMissionsList,
    timelineQueryStatus,
    timelineEntityId,
  } = useMissionContext();
  const { getDataCountry, dataCountry } = useSettingsContext();
  const {
    displayClientStatus,
    updateClient,
    clientsByIds,
    editClientStatus,
    deleteClient,
    getAssignedClients,
    assignedClients,
    getInitialAssignedClients,
    initialAssignedClient,
    getInitialAuthorizedManagers,
    initialAuthorizedManager,
    authorizedManager,
    getAuthorizedManagers,
    getClientBySlug,
    clientBySlug,
    setEditClientStatus,
    deleteClientStatus,
    setDeleteClientStatus,
  } = useClientContext();
  const isCurrentEntity = clientBySlug?.slug === slug;
  const {
    getHistoryBillingClientByClientId,
    setRefresh,
    refresh,
    billingClientStatus,
  } = useBillingClientDatatrackContext();
  const { lang, userScope, apiErrorResponse, setApiErrorResponse } =
    useUserContext();
  const { setSlugSearched } = useContext(PageContext);
  const { sectorListGetter, sectorList } = useSectorContext();
  const { branchListGetter, branchList } = useBranchContext();
  const { searchGeneralByProfiles, usersByProfiles, searchUsers, usersByIds } =
    useGeneralContext();
  const { createdFile, setCreatedFile, createFile } = useFileContext();
  const { personsList, getPersonsByIds } = usePersonContext();

  const [section, setSection] = useState(SECTIONS.GENERAL_INFO);
  const [readOnly, setReadOnly] = useState(true);
  const [isEnabled, setIsEnabled] = useState(false);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const size = useWindowSize();
  const [cellHighlight, setCellHighlight] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [isStrategicClient, setIsStrategicClient] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const currentQueryStatus = statusHandler({
    statusList: [editClientStatus, deleteClientStatus],
  });

  const validationModalTitle = validationModalTitleHelper({
    isEditAction: editClientStatus !== QUERY_STATUS.INITIAL,
    isDeleteAction: deleteClientStatus !== QUERY_STATUS.INITIAL,
  });

  useEffect(() => {
    getDataCountry();
  }, []);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (apiErrorResponse && !Array.isArray(apiErrorResponse)) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
      if (Object.keys(apiErrorResponse).length > 0) {
        setReadOnly(false);
      }
    }
  }, [apiErrorResponse, setError]);

  useEffect(() => {
    setReadOnly(true);
    setCreatedFile({});
    slug && getClientBySlug(slug);
  }, [slug, userScope]);

  useEffect(() => {
    if (displayClientStatus === QUERY_STATUS.NOT_FOUND) {
      navigate(`/${userScope}/not-found`, { replace: true });
    }
  }, [displayClientStatus, userScope, navigate]);

  const { picture: entityPicture } = useEntityProfilePicture(
    clientBySlug?.picture_file_id
  );

  // Use createdFile as fallback when entity picture is not available
  const picture = createdFile?.deleted
    ? null
    : entityPicture || createdFile?.uri || createdFile?.picture_file_id;
  useEffect(() => {
    resetTimeline();
    if (clientBySlug?.id) {
      getTimeline({ filters: { type: "client", id: clientBySlug.id } });
      getHistoryBillingClientByClientId({
        id: clientBySlug.id,
        scope: userScope,
      });
    }
  }, [clientBySlug]);

  const id = useMemo(() => clientBySlug?.id, [clientBySlug]);

  useEffect(() => {
    sectorListGetter();
    branchListGetter();
  }, [sectorListGetter, branchListGetter]);

  useEffect(() => {
    if (id) {
      getTimeline({ filters: { type: "client", id } });
    }
  }, [id]);

  useEffect(() => {
    switch (section) {
      case SECTIONS.MISSIONS:
        getTimeline({ filters: { type: "client", id } });
        break;
      case SECTIONS.GENERAL_INFO:
        currentQueryStatus === QUERY_STATUS.INITIAL && getClientBySlug(slug);
        break;
      default:
        break;
    }
  }, [id, slug, section]);

  const updateData = async (info) => {
    window.scrollTo(0, 0);
    // Awaited so the caller's submit lock covers the whole request
    try {
      await updateClient(id, {
        ...formatSentData(info),
        sector_id: _.chain(info.sector_id).head().get("value").value(),
        country: info?.country[0]?.value || info.country,
        strategic: isStrategicClient,
      });
    } catch {
      // Provider already set the entity status to ERROR and apiErrorResponse
    }
  };

  const onSubmit = async (info) => {
    await updateData({
      ...info,
      picture_file_id: createdFile?.deleted
        ? null
        : createdFile?.picture_file_id ||
          createdFile?.uri ||
          clientBySlug?.picture_file_id,
    });
  };

  const initHeader = () => {
    setIsEnabled(clientBySlug.enabled);
  };

  useEffect(() => {
    if (timelineMissionsList.length > 0) {
      const ids = _.chain(timelineMissionsList)
        .map((elm) => [elm.manager_id, elm.worker_id])
        .flattenDeep()
        .uniq()
        .join(",")
        .value();
      getPersonsByIds({ ids: ids });
    }
  }, [timelineMissionsList, id]);

  useEffect(() => {
    if (clientBySlug?.assigned_managers?.length > 0) {
      getInitialAuthorizedManagers({
        ids: clientBySlug?.assigned_managers,
      });
    }
    if (clientBySlug?.affiliate_id) {
      getInitialAssignedClients({ ids: clientBySlug?.affiliate_id.toString() });
    }
  }, [clientBySlug, userScope]);

  const getFormattedMission = (list) =>
    list.map((elm) => ({
      name: (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {elm?.mission_name}
          <br />
          {elm?.mission_analytical_code}
        </div>
      ),
      worker_id: (
        <div>
          <Link
            to={`/${userScope}/${
              elm?.worker_profile === "cslt" ? "consultant" : "subcontractor"
            }/${elm?.worker_id && elm?.worker_slug}`}
          >
            {elm?.worker_name}
          </Link>
          {elm?.worker_profile === "str" && <div>(STR)</div>}
        </div>
      ),

      manager_id: (
        <Link
          to={`/${userScope}/staff/${elm?.manager_slug}`}
          onClick={() => setSlugSearched(elm?.manager_slug)}
        >
          {elm?.manager_name}
        </Link>
      ),
      start_date: getFormattedDate(elm?.start_date, lang),
      end_date: (
        <div style={{ textAlign: "center" }}>
          {elm?.end_date === null ? "-" : getFormattedDate(elm?.end_date, lang)}
        </div>
      ),
      status: (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {getStatusLabel(elm.mission_status)}
          <Link to={`/${userScope}/missions/${elm?.mission_slug}`}>
            <FormattedMessage
              id="view_sheet"
              defaultMessage={"Voir la fiche"}
            />
          </Link>
        </div>
      ),
      name_for_search: [
        elm?.mission_name,
        elm?.worker_name,
        elm?.mission_analytical_code,
        elm?.worker_analytical_code,
      ]
        .filter(Boolean)
        .join(" "),
    }));

  useEffect(() => {
    currentQueryStatus !== QUERY_STATUS.INITIAL && setIsOpen(true);
    if (editClientStatus === QUERY_STATUS.SUCCESS) {
      getClientBySlug(clientBySlug?.slug);
    }
  }, [currentQueryStatus]);

  const value = useMemo(
    () => ({
      entitySlug: clientBySlug?.slug,
      entityStatus: billingClientStatus,
      editClientStatus,
      cellHighlight: cellHighlight,
      setIsValidated: setIsValidated,
      searchUsers,
      usersByIds,
      authorizedClients: personsList,
      authorizedManager: authorizedManager,
      branchList: branchList,
      clientBySlug: clientBySlug,
      clientsList: assignedClients,
      deleted: clientBySlug?.deleted,
      entity: ENTITY.CLIENT,
      getClients: ({ search, ids }) => getAssignedClients({ ids, search }),
      getManagers: (search) => getAuthorizedManagers({ search }),
      id: id,
      initialAssignedClient: initialAssignedClient,
      initialAuthorizedManager: initialAuthorizedManager,
      isEnabled: isEnabled,
      isMobile: size.width < 600,
      lang: lang,
      onDelete: deleteClient,
      personsList: _.concat(personsList, usersByProfiles),
      readOnly: readOnly,
      section: section,
      sectorList: sectorList,
      setIsEnabled: setIsEnabled,
      setReadOnly: setReadOnly,
      setSection: setSection,
      getOptions: (search) =>
        searchGeneralByProfiles({ profile: "staff", search }),
      isStrategicClient,
      setIsStrategicClient,
      createFile,
      createdFile,
      setCreatedFile,
      picture,
      setCode,
      setStatus,
      setRefresh,
      refresh,
      timelineMissionsList,
      timelineQueryStatus,
      dataCountry,
      isValidationModalOpen: isOpen,
      apiErrorResponse,
    }),
    [
      cellHighlight,
      editClientStatus,
      setIsValidated,
      assignedClients,
      authorizedManager,
      branchList,
      clientBySlug,
      id,
      initialAssignedClient,
      initialAuthorizedManager,
      isEnabled,
      lang,
      personsList,
      readOnly,
      section,
      sectorList,
      usersByProfiles,
      usersByIds,
      isStrategicClient,
      createFile,
      createdFile,
      setCreatedFile,
      picture,
      refresh,
      timelineMissionsList,
      timelineQueryStatus,
      dataCountry,
      isOpen,
      apiErrorResponse,
    ]
  );
  return (
    <InfoContext.Provider value={value}>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <ValidationModal
        open={isOpen}
        status={currentQueryStatus}
        onClose={() =>
          commonOnCloseHandler({
            editEntityStatus: editClientStatus,
            deleteEntityStatus: deleteClientStatus,
            setDeleteEntityStatus: setDeleteClientStatus,
            setEditEntityStatus: setEditClientStatus,
            setIsOpen,
            setReadOnly,
            userScope,
            navigate,
          })
        }
        messages={{
          pendingTitleId: validationModalTitle,
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: validationModalTitle,
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: validationModalTitle,
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
      />
      {(!isCurrentEntity ||
        ![
          QUERY_STATUS.SUCCESS,
          QUERY_STATUS.ERROR,
          QUERY_STATUS.NOT_FOUND,
        ].includes(displayClientStatus)) && <LoaderV2 loaderSize="fullpage" />}
      {isCurrentEntity && displayClientStatus === QUERY_STATUS.SUCCESS && (
        <>
          <InfoHeaderAction />
          {section === SECTIONS.GENERAL_INFO && (
            <ClientGeneralInfo
              readOnly={readOnly}
              setReadOnly={setReadOnly}
              onSubmit={onSubmit}
              initHeader={initHeader}
              getStatus={setStatus}
              getCode={setCode}
              status={status}
              code={code}
            />
          )}
          {section === SECTIONS.MISSIONS &&
            (timelineEntityId !== clientBySlug?.id ||
              timelineQueryStatus === QUERY_STATUS.LOADING) && (
              <LoaderV2 loaderSize="fullpage" />
            )}
          {section === SECTIONS.MISSIONS &&
            timelineEntityId === clientBySlug?.id &&
            timelineQueryStatus === QUERY_STATUS.SUCCESS && (
              <MissionsWorkers
                formattedMissions={getFormattedMission(timelineMissionsList)}
                role={ENTITY.CLIENT}
                workers={personsList}
                clients={clientsByIds}
                id="missionsClients"
              />
            )}
        </>
      )}
    </InfoContext.Provider>
  );
};

export default DisplayClient;
